<template>
  <v-form ref="form" v-model="valid" lazy>
    <v-dialog v-model="show" max-width="600px" scrollable>    
      <v-card>
        <v-card-title primary-title class="headline">
          Upgrade Application
          <v-progress-linear v-if="running || loadingTypeParams" :indeterminate="true"></v-progress-linear>
        </v-card-title>

        <v-card-text>
          <v-container grid-list-lg fluid>
            <v-layout wrap>
              <v-flex xs12>
                <v-text-field
                  :disabled="true"
                  label="Application Name"
                  v-model="appName"
                ></v-text-field>
              </v-flex>      

              <v-flex :class="[{xs6 : showVersions || showPrereleases}, {x12 : !(showVersions || showPrereleases) }]">
                <v-select
                  :disabled="true"
                  label="Application Type"
                  v-model="appTypeName"
                  :items="atmosAppTypes ? getFilteredApps(atmosAppTypes) : []"
                  item-text="description"
                  item-value="name"
                  @change="selectAppTypeName"
                ></v-select>
              </v-flex>      

              <v-flex xs6 v-if="showVersions || showPrereleases">
                <v-select
                  :disabled="running || loadingTypeParams || !showVersions"
                  label="Application Version"
                  v-model="appTypeVersion"
                  :items="filteredAppTypeVersions"
                  item-text="name"
                  item-value="value.version"
                  :rules="[rules.required]"
                  @change="selectAppTypeVersion"
                ></v-select>
              </v-flex>      

              <v-flex xs12>
                <v-tabs v-if="Object.keys(appTypeParams ? appTypeParams : {}).length > 0">
                  <template v-for="g in Object.keys(appTypeParams ? appTypeParams : {})">
                    <v-tab ripple :key="g">{{ appTypeParams[g].name }}</v-tab>
                    <v-tab-item :key="g">
                      <v-container grid-list-lg fluid>
                        <v-layout column>
                          <v-flex xs12>
                            <template v-for="param in appTypeParams[g].parameters">
                              <template v-if="param.type == 'string' && !param.allowedValues">
                                <v-text-field
                                  :disabled="running || loadingTypeParams"
                                  :key="param.name"
                                  :label="param.displayText"
                                  :rules="param.rules"
                                  v-model="param.value"
                                  clearable
                                  :hint="param.helpText"
                                  persistent-hint
                                >
                                </v-text-field>
                              </template>
                              <template v-else-if="param.type == 'string' && param.allowedValues">
                                <v-select
                                  :disabled="running || loadingTypeParams"
                                  :key="param.name"
                                  :label="param.displayText"
                                  :items="param.allowedValues"
                                  item-text="displayText"
                                  item-value="value"
                                  :rules="param.rules"
                                  v-model="param.value"
                                  clearable
                                  :hint="param.helpText"
                                  persistent-hint
                                >
                                </v-select>
                              </template>
                              <template v-else-if="param.type == 'boolean'">
                                <v-switch
                                  :disabled="running || loadingTypeParams"
                                  :key="param.name"
                                  :label="param.displayText"
                                  :rules="param.rules"
                                  v-model="param.value"
                                  :hint="param.helpText"
                                  persistent-hint
                                >
                                </v-switch>                              
                              </template>
                              <template v-else>
                                <v-textarea
                                  :disabled="running || loadingTypeParams"
                                  :key="param.name"
                                  :label="param.displayText"
                                  v-model="param.value"
                                  box
                                  :rules="param.rules"
                                  :hint="param.helpText"
                                  persistent-hint
                                >
                                </v-textarea>                              
                              </template>
                            </template>                          
                          </v-flex>
                        </v-layout>                      
                      </v-container>
                    </v-tab-item>
                  </template>
                </v-tabs>
              </v-flex>

            </v-layout>
          </v-container>      
        </v-card-text>

        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn 
            :disabled="running || loadingTypeParams || !valid"
            color="primary" 
            flat @click.stop="handleOK"
          >OK</v-btn>
          <v-btn 
            color="primary" 
            flat @click.stop="handleClose"
          >Close</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>  
  </v-form>  
</template>

<script>
import Vue from "vue";
import semver from "semver";
import store from "@/store";
import { Atmos } from "@/apis/atmos";
import { mapGetters, mapActions } from "vuex";

export default {
  name: "dialog-upgrade-application",

  data() {
    return {
      appName: null,
      appTypeName: null,
      appTypeVersion: null,
      appTypeParams: null,
      loadingTypeParams: false,
      running: false,      
      valid: false,

      rules: {
        required: v => !!v || 'Value is required.',
        minLength(len) { return v => (v || '').length > len || `Value is too short. Must be greater than ${len} characters.` },
        maxLength(len) { return v => (v || '').length < len || `Value is too long. Must be less than than ${len} characters.` },
      },
    };
  },

  computed: {
    ...mapGetters('atmos', [
      "atmosSubscription", 
      "atmosAppTypes",
      "showPrereleases",
      "showVersions"]),

    filteredAppTypeVersions() {
      if (this.appTypeName) {      
        var list = null;
        if (this.showPrereleases) {
          list = this.atmosAppTypes[this.appTypeName].versions
        } else {
          list = this.atmosAppTypes[this.appTypeName].versions.filter(v => semver.valid(v.version) && !semver.prerelease(v.version));
        }

        list = list.map(i => { return { name:i.version, value:i } } );
        list = list.sort((a,b) => { 
          if (semver.valid(a) && semver.valid(b)) {
            return semver.lt(a.value, b.value) 
          }
          return 1;
        });

        if (list.length > 0) {
          list[0].name = `Latest (${list[0].name})`;
        }

        return list;
      } else {
        return [];
      }
    },

    app: {
      get() {
        return this.value.app;
      }
    },

    show: {
      get() {
        return this.value.show;
      },
      set(value) {        
        var val = Object.assign({}, this.value);
        val.show = value;
        this.$emit('input', val);
      }
    }
  },

  watch: {
    show: function (val, oldVal) {
      var self = this;
      if (val) {

        self.appName = null;
        self.appTypeName = null;
        self.appTypeVersion = null;
        self.appTypeParams = null;
        self.loadingTypeParams = false,
        self.running = false;
        self.$refs.form.reset();

        Vue.nextTick(function() {         
          self.appName = self.value.app.name;
          self.appTypeName = self.value.app.type;
          self.appTypeOrigParams = self.value.app.parameters;          
          self.selectAppTypeName();
        });
      }
    }
  },

  props: {
    value: Object,
  },

  methods: {
    ...mapActions('atmos',
      ["atmosUpgradeApplication"]),

    getFilteredApps(appTypes) {
      return Object.keys(appTypes)
          .filter(i => !appTypes[i].hidden && i == "SoftPro.Select")          
          .map(i => appTypes[i]);
    },

    async updateAppTypeParams() {
      this.loadingTypeParams = true;
      try {
        var groups = {};
        if (this.appTypeName != null && this.appTypeVersion != null) {
          
          //get the manifest for the type
          var manifest = await Atmos.getApplicationTypeManifest(this.appTypeName, this.appTypeVersion);
          if (manifest.parameters) {
            groups = { 
              general: { 
                name: "General",
                parameters: {}
              }
            }

            //sort the parameters into their groupings
            Object.keys(manifest.parameters).forEach(i => {
              var item = manifest.parameters[i];
              //hide hidden parameters
              if (item.hidden) {
                return;
              }

              var groupName = item.group ? item.group.toLowerCase() : "general";
              var group = groups[groupName];
              if (!group) {
                group = {
                  name: item.group,
                  parameters: {}
                };
                groups[groupName] = group;
              };

              //fixup any old or missing values
              if (!item.displayText) {
                item.displayText = i;
              }
              
              if (item.allowedValues) {
                item.allowedValues = item.allowedValues.map(i => {
                  if (typeof i == "string") {
                    return {
                      value: i,
                      displayText: i,
                      helpText: null
                    };
                  } else {
                    return i;
                  }
                });
              }

              //add the rules
              var rules = [];
              if (item.required) {
                rules.push(value => !!value || 'Required.');
              }

              //add the parameters to the group
              var param = {
                name: i,
                value: this.appTypeOrigParams.hasOwnProperty(i) ? this.appTypeOrigParams[i] : item.defaultValue,
                rules: rules,
                ...item
              };

              group.parameters[param.name.toLowerCase()] = param;
            });

            //remove empty groups
            Object.keys(groups).forEach(g =>
            {
              if (Object.keys(groups[g].parameters).length == 0) {
                delete groups[g];
              }
            });

          }
        }
        this.appTypeParams = groups;
      } finally {
        this.loadingTypeParams = false;        
      }
    },
    
    handleClose() {
      this.show = false;
    },

    async handleOK() {
      this.running = true;
      try {
        
        //gather the set parameters
        var parameters = {};
        Object.keys(this.appTypeParams).forEach(g => {
          Object.keys(this.appTypeParams[g].parameters).forEach(p => {
            var param = this.appTypeParams[g].parameters[p];
            if (param.value) {
              parameters[param.name] = param.value;
            }
          })
        })

        await this.atmosUpgradeApplication({
          name: this.appName,
          version: this.appTypeVersion,
          parameters: parameters,
        });

        this.show = false;
      } catch (e) {
        alert(e);
      } finally {
        this.running = false;
      }
    },

    selectAppTypeName() {
      this.appTypeVersion = this.filteredAppTypeVersions.length > 0 ? this.filteredAppTypeVersions[0].value.version : null;
      this.updateAppTypeParams();
    },

    selectAppTypeVersion() {
      this.updateAppTypeParams();
    }
  }
};
</script>

<style scoped>
</style>